import React, { useState } from "react";
import { Tag, Check, X } from "lucide-react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Reusable promo-code input for /book and /booking/:id.
 *
 * Props:
 *   serviceId     – required, what we validate the coupon against
 *   originalAmount – required, used only to render the "خصم" preview
 *   onApplied     – ({code, discount, finalAmount}) => void  (called on success)
 *   onCleared     – () => void                               (called on remove)
 */
const PromoCodeInput = ({ serviceId, originalAmount, onApplied, onCleared }) => {
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null); // {code, discount, final_amount}

  const apply = async () => {
    const trimmed = (code || "").trim();
    if (!trimmed) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: trimmed,
        service_id: serviceId,
      });
      setApplied(data);
      onApplied &&
        onApplied({
          code: data.code,
          discount: data.discount,
          finalAmount: data.final_amount,
        });
      toast.success(
        data.final_amount === 0
          ? "تم تطبيق كوبون مجاني — لن يُطلب منك الدفع"
          : "تم تطبيق كود الخصم بنجاح"
      );
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "كود غير صالح"));
    } finally {
      setApplying(false);
    }
  };

  const clear = () => {
    setApplied(null);
    setCode("");
    onCleared && onCleared();
  };

  if (applied) {
    return (
      <div
        className="rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] p-4 flex items-center justify-between"
        data-testid="promo-applied"
      >
        <div className="flex items-center gap-2 text-[#047857]">
          <Check className="w-4 h-4 shrink-0" />
          <span className="text-sm">
            تم تطبيق{" "}
            <span className="font-mono font-semibold" dir="ltr">
              {applied.code}
            </span>{" "}
            — خصم{" "}
            <span dir="ltr">{Number(applied.discount).toLocaleString("ar-SA")}</span>{" "}
            ر.س
          </span>
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-[#047857] hover:text-[#065F46]"
          data-testid="promo-clear-btn"
          aria-label="إلغاء كود الخصم"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#5E5C58] flex items-center gap-1.5">
        <Tag className="w-4 h-4 text-[#0A4D3C]" /> هل لديك كود خصم؟
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="مثال: SAUDI94"
          dir="ltr"
          className="flex-1 rounded-xl border border-[#E5E1D8] bg-[#FAF9F6] px-4 py-2 text-base text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#0A4D3C]/30"
          data-testid="promo-code-input"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              apply();
            }
          }}
        />
        <Button
          type="button"
          onClick={apply}
          disabled={applying || !code.trim()}
          className="rounded-xl bg-[#0A4D3C] hover:bg-[#063A2D] text-white px-5"
          data-testid="promo-apply-btn"
        >
          {applying ? "..." : "تطبيق"}
        </Button>
      </div>
    </div>
  );
};

export default PromoCodeInput;
